import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import * as moment from 'moment-timezone';
import { BehaviorSubject, combineLatest, Observable, of, race, timer } from 'rxjs';
import { filter, map, mapTo, switchMapTo, take } from 'rxjs/operators';
import { FracClusterTruckingVendor } from '~lmo/models/frac.model';
import { LmoFracsService } from '~lmo/services';
import { LmoFracFctvsService, OverflowVendor } from '~lmo/services/lmo-frac-fctvs.service';

@Component({
  selector: 'sa-lmo-frac-home-auto-dispatch-toggle',
  styleUrls: ['./lmo-frac-home-auto-dispatch-toggle.component.scss'],
  templateUrl: './lmo-frac-home-auto-dispatch-toggle.component.html',
})
export class LmoFracHomeAutoDispatchToggleComponent implements OnInit {
  public enabled$: Observable<boolean>;
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public lastToggledBy$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public lastToggledDate$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public lastToggledTime$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public currentTimezone: string;
  public hasHistory$ = new BehaviorSubject<boolean>(false);
  public fracClusterTruckingVendors$: Observable<FracClusterTruckingVendor[]>;
  public isLoHiAwarded$ = new BehaviorSubject<boolean>(false);
  public isLoHiOverflow$ = new BehaviorSubject<boolean>(false);
  public isLohiAwardedOrOverflow$: Observable<boolean>;
  private lohiLogisticsID = 958;

  constructor(
    private fracService: LmoFracsService,
    private fracFctvsService: LmoFracFctvsService,
    private datePipe: DatePipe,
  ) {
    this.currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.fracClusterTruckingVendors$ = race(
      this.fracFctvsService.currentFracFctvs$.pipe(filter((fctvs) => !!fctvs.length)),
      timer(3000, 0).pipe(take(1), switchMapTo(this.fracFctvsService.currentFracFctvs$)),
    ).pipe(
      map((fctvs) => fctvs.filter((fctv) => fctv.status === 'awarded')),
      map((fctvs) => fctvs.filter((fctv) => fctv.allocationPercent !== 0)),
    );

    this.fracClusterTruckingVendors$.pipe().subscribe((fctvs) => {
      const loHiAwarded = fctvs.find((fctv) => fctv.truckingVendorId === this.lohiLogisticsID);
      if (loHiAwarded) {
        this.isLoHiAwarded$.next(true);
      }
    });
  }

  public ngOnInit() {
    this.getAutoDispatchState();
    this.setLoHiAwardedOrOverflowStatus();
  }

  private getAutoDispatchState() {
    const result$ = this.fracService.getAutoDispatchState$();
    this.enabled$ = result$.pipe(
      map((resp) => {
        if (resp) {
          return !resp.disabled;
        } else {
          return true;
        }
      }),
    );
    result$.subscribe((resp) => {
      if (resp) {
        this.lastToggledBy$.next(resp.lastToggledBy);
        this.lastToggledDate$.next(this.convertToLocalTime(resp.createdAt).date);
        this.lastToggledTime$.next(this.convertToLocalTime(resp.createdAt).time);
        this.hasHistory$.next(true);
      } else {
        this.hasHistory$.next(false);
      }
    });
  }

  public toggleStateChanged(event: any) {
    this.isLoading$.next(true);
    this.fracService.toggleAutoDispatch$(!event.checked).subscribe((resp) => {
      this.enabled$ = of(!resp.disabled);
      this.isLoading$.next(false);
      this.lastToggledBy$.next(resp.lastToggledBy);
      this.lastToggledDate$.next(this.convertToLocalTime(resp.createdAt).date);
      this.lastToggledTime$.next(this.convertToLocalTime(resp.createdAt).time);
    });
  }

  private convertToLocalTime(utcDateString: string): { date: string; time: string } {
    const date = new Date(utcDateString);
    const formattedDate = this.datePipe.transform(date, 'M/d/yyyy');
    const formattedTime = this.datePipe.transform(date, 'h:mm a');
    const timezoneAbbreviation = moment.tz(date, Intl.DateTimeFormat().resolvedOptions().timeZone).format('z');
    const formattedTimeWithTimezone = `${formattedTime} ${timezoneAbbreviation}`;
    return { date: formattedDate, time: formattedTimeWithTimezone };
  }

  private setLoHiAwardedOrOverflowStatus() {
    race(
      this.fracFctvsService.currentFracOverflowVendors$.pipe(filter((vendors) => !!vendors && !!vendors.length)),
      timer(5000).pipe(mapTo([] as OverflowVendor[])),
    ).subscribe((vendors) => {
      const loHiOverflow = vendors.find((vendor) => vendor.vendorId === this.lohiLogisticsID);
      if (loHiOverflow) {
        this.isLoHiOverflow$.next(true);
      }
    });

    const combinedObservable$ = combineLatest([this.isLoHiAwarded$, this.isLoHiOverflow$]).pipe(
      map(([isLoHiAwarded, isLoHiOverflow]) => ({ isLoHiAwarded, isLoHiOverflow })),
    );

    combinedObservable$.subscribe({
      next: (combinedValues) => {
        if (combinedValues.isLoHiAwarded || combinedValues.isLoHiOverflow) {
          this.isLohiAwardedOrOverflow$ = of(true);
        } else {
          this.isLohiAwardedOrOverflow$ = of(false);
        }
      },
    });
  }
}
